import { Links } from 'application/constants/links.ts';
import { SearchResultDto } from 'services/api/api-client.types.ts';
import React from 'react';

type QueryParam = {
  name: string;
  isExtractionNeeded?: boolean;
};

type PageInfo = {
  pageLink: string;
  queryParams?: Record<string, QueryParam>;
};

const infosystemMap: Record<string, PageInfo> = {
  'Price lists': {
    pageLink: Links.Authorized.PriceLists.route,
  },
  'Service and repair': {
    pageLink: Links.Authorized.ServiceAndRepair.route,
    queryParams: {
      FileName: {
        name: 'name',
      },
    },
  },
  'Commercial terms': {
    pageLink: Links.Authorized.PriceLists.route,
  },
  //Todo: find out what is 004 in abas
  '004': {
    pageLink: '/',
  },
  'Tech updates': {
    pageLink: Links.Authorized.TechUpdates.route,
    queryParams: {
      FileName: {
        name: 'title',
      },
    },
  },
  'Service presentation': {
    pageLink: Links.Authorized.ServicePresentation.route,
    queryParams: {
      FileName: {
        name: 'name',
      },
    },
  },
  'Serial number': {
    pageLink: Links.Authorized.SNLots.route,
    queryParams: {
      FileName: {
        name: 'number',
        isExtractionNeeded: true,
      },
    },
  },
  'Telehealth billing': {
    pageLink: Links.Authorized.TelehealthBilling.route,
    queryParams: {
      FileName: {
        name: 'invoice',
        isExtractionNeeded: true,
      },
    },
  },
  Qualifications: {
    pageLink: Links.Authorized.Qualifications.route,
    queryParams: {
      Description: {
        name: 'training',
      },
      Participant: {
        name: 'participant',
      },
    },
  },
  'Warranty request': {
    pageLink: Links.Authorized.WarrantyRequest.route,
  },
  'Warranty request history': {
    pageLink: Links.Authorized.WarrantyRequestHistory.route,
    queryParams: {
      OrderNumber: {
        name: 'orderNumber',
      },
      Reference: {
        name: 'reference',
      },
    },
  },
  'Device overview': {
    pageLink: Links.Authorized.DeviceOverview.route,
  },
  'User management': {
    pageLink: Links.Authorized.UserManagement.route,
  },
  'Order list': {
    pageLink: Links.Authorized.OrderList.route,
    queryParams: {
      Order: {
        name: 'orderNumber',
        isExtractionNeeded: true,
      },
      YourReference: {
        name: 'reference',
      },
    },
  },
  'Outstanding receivables': {
    pageLink: Links.Authorized.OutstandingReceivables.route,
    queryParams: {
      InvoiceNumber: {
        name: 'invoiceNumber',
      },
    },
  },
  Repairs: {
    pageLink: Links.Authorized.Repairs.route,
    queryParams: {
      Order: {
        name: 'incomingNumber',
      },
    },
  },
  'Warranty info': {
    pageLink: Links.Authorized.WarrantyInfo.route,
  },
  'Certificate generation for mainboard': {
    pageLink: Links.Authorized.MainboardReplacerForm.route,
  },
  'Advanced search': {
    pageLink: Links.Authorized.AdvancedSearch.route,
    queryParams: {
      searchValue: {
        name: 'searchValue',
      },
    },
  },
};

const extractFirst8Digit = (text: string): string | null => {
  const match = text.match(/\b\d{8}\b/);
  return match ? match[0] : null;
};

export const buildUrl = (option: SearchResultDto) => {
  const info = infosystemMap[option.pageName];
  if (!info) {
    return option.cmsPageUrl ?? '#';
  }

  const base = info.pageLink;
  const queryParams = info.queryParams;
  if (!queryParams) return base;

  const params = new URLSearchParams();

  for (const key of Object.keys(option.matchedFields)) {
    const param = queryParams[key];
    const value = option.matchedFields[key];

    if (param) {
      let paramValue = param.isExtractionNeeded
        ? extractFirst8Digit(value) ?? ''
        : value;

      // removing language(EN,DE..) in filenames of Service and repair
      if (option.pageName === 'Service and repair') {
        paramValue = paramValue.trimEnd();

        paramValue = paramValue.replace(/\s?(?:DE|EN|FR|IT|ES\(MX\)|ES)$/i, '');
      }

      paramValue = paramValue.replace(/\\n/g, ' ').replace(/\r?\n/g, ' ');
      params.set(param.name, paramValue);
    }
  }

  return `${base}${params.toString() ? `?${params.toString()}` : ''}`;
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const highlightMatches = (
  text: string,
  query: string,
  highlightClass?: string,
): React.ReactNode[] => {
  if (!text) return [<span key={0}>{text}</span>];
  const q = query?.trim();
  if (!q) return [<span key={0}>{text}</span>];

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  const escapedTokens = tokens.map(escapeRegExp);

  if (tokens.length === 0) return [<span key={0}>{text}</span>];

  const pattern = `(${escapedTokens.join('|')})`;
  const re = new RegExp(pattern, 'gi');

  const parts = text.split(re);

  return parts.map((part, i) => {
    if (!part) return <React.Fragment key={i} />;

    const isMatchedPart = tokens.some(
      (t) => part.toLowerCase() === t.toLowerCase(),
    );

    if (isMatchedPart) {
      return (
        <span key={i} className={highlightClass ?? undefined}>
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};
